import { Button, confirm, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { HelpCircle, PlusIcon } from "lucide-react";
import { useCallback, useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useDeleteLeaveTypeMutation, useLeaveTypesQuery } from "../api/queries";
import { LeaveTypesTour, useLeaveTypesTour } from "../tour/LeaveTypesTour";
import {
  LeaveTypesFilter,
  type LeaveTypesSearchParams,
} from "../ui/LeaveTypesFilter";
import { LeaveTypesTable } from "../ui/LeaveTypesTable";

export function LeaveTypesPage() {
  const nav = useNavigate({ from: "/app/hr/leave/types" });
  const search = useSearch({
    from: "/app/hr/leave/types",
  }) as LeaveTypesSearchParams & {
    offset?: number;
    limit?: number;
    sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  };

  const sortBy = search.sort?.[0]?.field ?? "orderIndex";
  const sortOrder = (search.sort?.[0]?.dir ?? "asc") as "asc" | "desc";

  const query = useMemo(
    () => ({
      offset: search.offset ?? 0,
      limit: search.limit ?? 20,
      sort: search.sort,
      isActive: search.isActive,
      search: search.search,
    }),
    [search.offset, search.limit, search.sort, search.isActive, search.search],
  );

  const listQuery = useLeaveTypesQuery(query);
  const deleteMutation = useDeleteLeaveTypeMutation();
  const data = listQuery.data?.data ?? [];

  const handleDelete = async (row: { id: string; name: string }) => {
    const ok = await confirm({
      title: "ລຶບປະເພດການລາ",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ "${row.name}"? ການກະທຳນີ້ບໍ່ສາມາດກູ້ຄືນໄດ້.`,
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    deleteMutation.mutate(row.id, {
      onSuccess: () => toast.success("ລຶບປະເພດການລາສຳເລັດ"),
      onError: (err: { message?: string }) =>
        toast.error(err?.message ?? "ລຶບບໍ່ສຳເລັດ"),
    });
  };
  const meta = listQuery.data?.meta;
  const totalCount = meta?.total ?? 0;
  const limit = meta?.limit ?? 20;
  const offset = search.offset ?? 0;

  const handlePaginationChange = (newOffset: number, newLimit: number) => {
    nav({
      search: {
        offset: newOffset,
        limit: newLimit,
        sort: search.sort,
        isActive: search.isActive,
        search: search.search,
      },
    });
  };

  const handleView = (row: { id: string }) => {
    nav({ to: "/app/hr/leave/types/$id", params: { id: row.id } });
  };

  const handleEdit = (row: { id: string }) => {
    nav({ to: "/app/hr/leave/types/$id/edit", params: { id: row.id } });
  };

  const handleNewType = () => {
    nav({ to: "/app/hr/leave/types/new" });
  };

  const handleSortingChange = useCallback(
    (id: string, desc: boolean) => {
      nav({
        search: {
          offset: 0,
          limit: search.limit ?? 20,
          sort: [{ field: id, dir: (desc ? "desc" : "asc") as "asc" | "desc" }],
          isActive: search.isActive,
          search: search.search,
        },
      });
    },
    [nav, search.limit, search.isActive, search.search],
  );

  const tour = useLeaveTypesTour();

  return (
    <>
      <Header />
      <Main>
        <LeaveTypesTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="mb-6" data-tourid="leave-types-toolbar">
          <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-semibold text-2xl">ປະເພດການລາ</h1>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 rounded-full"
                    onClick={tour.startTour}
                    title="ເລີ່ມທົດສອບການນຳທາງ"
                    aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                  >
                    <HelpCircle className="size-5" />
                  </Button>
                </div>
                <p className="text-muted-foreground text-sm">
                  ຕັ້ງຄ່າປະເພດລາ (ລາກິດ ລາພັກ ລາບໍ່ໄດ້ຮັບເງິນ ຯລຯ) ແລະ ສິດລາຕໍ່ປີ.
                </p>
              </div>
            </div>
            <Button onClick={handleNewType}>
              <PlusIcon className="h-4 w-4" />
              ສ້າງປະເພດການລາ
            </Button>
          </div>
        </div>

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          <LeaveTypesFilter
            searchParams={search}
            fromRoute="/app/hr/leave/types"
          />
          <div data-tourid="leave-types-table">
            <LeaveTypesTable
              data={data}
              isLoading={listQuery.isLoading}
              offset={offset}
              limit={limit}
              totalCount={totalCount}
              onPaginationChange={handlePaginationChange}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortingChange={handleSortingChange}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </div>
        </div>
      </Main>
    </>
  );
}
